import type { SubmitHandler } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { createSequence } from '@olivierpascal/helpers';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IUserContentReviewListFilterFormBodyValues } from '~/components/forms/UserContentReviewListFilterForm';
import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import { Table } from '~/components/molecules/Table';
import {
  UserContentReviewListItem,
  UserContentReviewListItemSkeleton,
} from '~/components/molecules/UserContentReviewListItem';
import { usePagination } from '~/hooks/usePagination';
import { useUserContentQuery } from '~/lib/api/modules/UserContent/hook/useUserContentQuery';
import { UserContentReviewListAside } from './UserContentReviewListAside';

const GRID_TEMPLATE_COLUMNS = '1fr repeat(2, min-content)';
const DEFAULT_SKELETON_COUNT = 10;

export type IUserContentReviewListProps = IContainerProps & {
  appId: string;
};

type IUserContentReviewListFilters = IUserContentReviewListFilterFormBodyValues;

export const UserContentReviewList: React.FC<IUserContentReviewListProps> = (
  props,
) => {
  const { sx, appId } = props;
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<IUserContentReviewListFilters>({
    toReview: searchParams.get('toReview') ? true : undefined,
    reported: searchParams.get('reported') ? true : undefined,
    moderated: searchParams.get('moderated') ? true : undefined,
    ugcId: searchParams.get('ugcId') ?? undefined,
    userId: searchParams.get('userId') ?? undefined,
    parentId: searchParams.get('parentId') ?? undefined,
    type:
      (searchParams.get(
        'type',
      ) as IUserContentReviewListFilterFormBodyValues['type']) ?? undefined,
  });
  const pagination = usePagination({ extraParams: filters });
  const userContentQuery = useUserContentQuery(
    pagination.limit > 0
      ? {
          appId,
          body: {
            start: pagination.start,
            limit: pagination.limit,
            moderated: filters.moderated,
            reported: filters.reported,
            reviewed: filters.toReview ? false : filters.toReview,
            type: filters.type,
            ugcId: filters.ugcId,
            userId: filters.userId,
            parentId: filters.parentId,
          },
        }
      : undefined,
    {
      keepPreviousData: true,
    },
  );
  const { t } = useTranslation();

  const filtersForm = useForm<IUserContentReviewListFilterFormBodyValues>({
    defaultValues: {
      ...filters,
    },
  });

  const handleFilterFormSubmit: SubmitHandler<IUserContentReviewListFilterFormBodyValues> =
    useCallback(
      (values) => {
        setFilters((filters) => ({
          ...filters,
          toReview: values.toReview || undefined,
          reported: values.reported || undefined,
          moderated: values.moderated || undefined,
          ugcId: values.ugcId,
          userId: values.userId,
          parentId: values.parentId,
          type: values.type,
        }));
        pagination.updatePage(0);
      },
      [pagination],
    );

  const handleFilterFormReset = useCallback((): void => {
    setFilters({});
    filtersForm.setValue('toReview', undefined);
    filtersForm.setValue('reported', undefined);
    filtersForm.setValue('moderated', undefined);
    filtersForm.setValue('ugcId', '');
    filtersForm.setValue('userId', '');
    filtersForm.setValue('parentId', '');
    filtersForm.setValue('type', undefined);
  }, [filtersForm]);

  const totalCount = userContentQuery.data?.totalCount;
  const count = userContentQuery.data?.items?.length;
  const isEmpty = !userContentQuery.isLoading && !totalCount;

  return (
    <>
      <ContentBlock sx={sx}>
        {userContentQuery.isError ? (
          <ContentBlock.Body first>
            <ErrorMessage error={userContentQuery.error} />
          </ContentBlock.Body>
        ) : isEmpty ? (
          <ContentBlock.Empty title={t('UserContentReviewList.empty.title')} />
        ) : (
          <>
            <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
              {userContentQuery.isLoading || userContentQuery.isPreviousData
                ? createSequence(count ?? DEFAULT_SKELETON_COUNT).map(
                    (index) => (
                      <UserContentReviewListItemSkeleton
                        key={index}
                        first={index === 0}
                      />
                    ),
                  )
                : userContentQuery.data?.items?.map(
                    (userContent, userContentIndex) => (
                      <UserContentReviewListItem
                        key={userContent._id}
                        appId={appId}
                        userContent={userContent}
                        first={userContentIndex === 0}
                      />
                    ),
                  )}
            </Table>

            <ContentBlock.Footer>
              <Pagination pagination={pagination} total={totalCount} />
            </ContentBlock.Footer>
          </>
        )}
      </ContentBlock>

      <UserContentReviewListAside
        appId={appId}
        form={filtersForm}
        onSubmit={handleFilterFormSubmit}
        onReset={handleFilterFormReset}
        loading={userContentQuery.isFetching}
      />
    </>
  );
};
